import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useAuth } from "@/auth";
import { useAdmin } from "@/hooks/useAdmin";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Send, Trash2, Sparkles, Heart, Flame, Clock, Star, Users, ChevronDown, ChevronUp, CornerDownRight, Shield } from "lucide-react";
import { toast } from "sonner";
import { celebrateAchievements } from "@/lib/achievements";

/**
 * Combined Rating (1-5 stars) + Comments thread for a single recipe.
 */
export default function RecipeFeedback({ recipeId }) {
  const { isRTL, lang } = useI18n();
  const { user } = useAuth();
  const isAdmin = useAdmin();
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sort, setSort] = useState("top");
  const [hover, setHover] = useState(0);
  const [showRaters, setShowRaters] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySubmitting, setReplySubmitting] = useState(false);

  const { data: feedback } = useQuery({
    queryKey: ["recipe", recipeId, "feedback"],
    queryFn: () => endpoints.getRecipeFeedback(recipeId),
    enabled: !!recipeId,
  });
  const { data: comments = [], isLoading } = useQuery({
    queryKey: ["recipe", recipeId, "comments", sort],
    queryFn: () => endpoints.listRecipeComments(recipeId, sort),
    enabled: !!recipeId,
  });

  // Scroll to a specific comment when URL hash points at it (admin "go to source" feature).
  useEffect(() => {
    if (!comments?.length) return;
    const hash = (typeof window !== "undefined" && window.location.hash) || "";
    const m = hash.match(/^#comment-(.+)$/);
    if (!m) return;
    const id = m[1];
    const el = document.getElementById(`comment-${id}`);
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-[#C86240]", "ring-offset-2");
        setTimeout(() => el.classList.remove("ring-2", "ring-[#C86240]", "ring-offset-2"), 2400);
      }, 200);
    }
  }, [comments?.length]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["recipe", recipeId] });
  };

  const setRating = async (n) => {
    if (!user) {
      toast.error(isRTL ? "سجّل دخولك للتقييم" : "Sign in to rate");
      return;
    }
    try {
      if (feedback?.user_rating === n) {
        await endpoints.unrateRecipe(recipeId);
        toast.success(isRTL ? "تم إلغاء تقييمك" : "Rating removed");
      } else {
        await endpoints.rateRecipe(recipeId, n);
        toast.success(isRTL ? `تم تقييم ${n} نجوم` : `Rated ${n} stars`);
      }
      invalidate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر التقييم" : "Rating failed"));
    }
  };

  const submit = async () => {
    const t = text.trim();
    if (t.length < 3) {
      toast.error(isRTL ? "اكتب جملة قصيرة" : "Write a short sentence");
      return;
    }
    setSubmitting(true);
    try {
      const created = await endpoints.createRecipeComment({ recipe_id: recipeId, text: t });
      setText("");
      invalidate();
      toast.success(isRTL ? "تم نشر تعليقك" : "Posted");
      celebrateAchievements(created?.new_achievements, lang);
      qc.invalidateQueries({ queryKey: ["my-achievements"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر النشر" : "Failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const submitReply = async (parentId) => {
    if (!user) {
      toast.error(isRTL ? "سجّل دخولك للرد" : "Sign in to reply");
      return;
    }
    const t = replyText.trim();
    if (t.length < 3) {
      toast.error(isRTL ? "اكتب جملة قصيرة" : "Write a short sentence");
      return;
    }
    setReplySubmitting(true);
    try {
      await endpoints.createRecipeComment({ recipe_id: recipeId, text: t, parent_id: parentId });
      setReplyText("");
      setReplyTo(null);
      invalidate();
      toast.success(isRTL ? "تم نشر الرد" : "Reply posted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isRTL ? "تعذّر النشر" : "Failed"));
    } finally {
      setReplySubmitting(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(isRTL ? "حذف هذا التعليق؟" : "Delete this comment?")) return;
    try {
      await endpoints.deleteRecipeComment(id);
      invalidate();
      toast.success(isRTL ? "حُذف" : "Deleted");
    } catch {
      toast.error(isRTL ? "تعذّر الحذف" : "Delete failed");
    }
  };

  const like = async (id) => {
    if (!user) {
      toast.error(isRTL ? "سجّل دخولك للتفاعل" : "Sign in to react");
      return;
    }
    qc.setQueriesData({ queryKey: ["recipe", recipeId, "comments"] }, (old) => {
      if (!Array.isArray(old)) return old;
      return old.map((c) =>
        c.id === id ? { ...c, liked: !c.liked, likes: (c.likes || 0) + (c.liked ? -1 : 1) } : c
      );
    });
    try {
      await endpoints.likeRecipeComment(id);
    } catch {
      invalidate();
    }
  };

  const avg = feedback?.avg || 0;
  const count = feedback?.count || 0;
  const myRating = feedback?.user_rating || 0;
  const raters = feedback?.raters || [];

  // Split comments into top-level + replies grouped by parent.
  const topLevel = comments.filter((c) => !c.parent_id);
  const repliesByParent = comments.reduce((acc, c) => {
    if (c.parent_id) {
      (acc[c.parent_id] = acc[c.parent_id] || []).push(c);
    }
    return acc;
  }, {});

  const renderComment = (c, isReply = false) => {
    const replies = repliesByParent[c.id] || [];
    return (
      <div
        key={c.id}
        id={`comment-${c.id}`}
        className={`scroll-mt-24 ${isReply ? "" : "space-y-2"}`}
        data-testid={`recipe-comment-${c.id}`}
      >
        <div
          className={`flex gap-3 group p-2 -mx-2 rounded-lg transition-shadow ${
            c.is_admin
              ? "bg-[#FFF4EE] border border-[#C86240]/30 ring-1 ring-[#C86240]/10"
              : ""
          }`}
        >
          {c.user_picture ? (
            <img src={c.user_picture} alt="" className={`${isReply ? "w-7 h-7" : "w-9 h-9"} rounded-full border border-[#2A2421]/10 shrink-0`} />
          ) : (
            <div className={`${isReply ? "w-7 h-7 text-xs" : "w-9 h-9 text-sm"} rounded-full bg-[#C86240]/15 flex items-center justify-center text-[#C86240] font-semibold shrink-0`}>
              {(c.user_name || "?").charAt(0)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-xs flex-wrap">
              <span className="font-semibold text-[#2A2421]">{c.user_name}</span>
              {c.is_admin && (
                <span className="inline-flex items-center gap-1 bg-[#C86240] text-white px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide">
                  <Shield className="w-2.5 h-2.5" />
                  {isRTL ? "إدارة" : "Admin"}
                </span>
              )}
              <span className="text-[#5C544F]">·</span>
              <span className="text-[#5C544F]">{new Date(c.created_at).toLocaleDateString()}</span>
            </div>
            <div className="mt-1 text-sm text-[#2A2421] whitespace-pre-wrap leading-relaxed">{c.text}</div>
            <div className="mt-1.5 flex items-center gap-3">
              <button
                onClick={() => like(c.id)}
                className={`inline-flex items-center gap-1 text-xs transition-colors ${c.liked ? "text-[#C86240]" : "text-[#5C544F] hover:text-[#C86240]"}`}
                data-testid={`recipe-comment-like-${c.id}`}
              >
                <Heart className={`w-3.5 h-3.5 ${c.liked ? "fill-[#C86240]" : ""}`} />
                <span>{c.likes || 0}</span>
              </button>
              {!isReply && user && (
                <button
                  onClick={() => {
                    setReplyTo(replyTo === c.id ? null : c.id);
                    setReplyText("");
                  }}
                  className="inline-flex items-center gap-1 text-xs text-[#5C544F] hover:text-[#C86240] transition-colors"
                  data-testid={`recipe-comment-reply-${c.id}`}
                >
                  <CornerDownRight className="w-3.5 h-3.5" />
                  {isRTL ? "رد" : "Reply"}
                </button>
              )}
            </div>
          </div>
          {(isAdmin || (user && c.user_id === user.user_id)) && (
            <button
              onClick={() => remove(c.id)}
              className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 p-1 self-start"
              data-testid={`recipe-comment-delete-${c.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* Replies + reply box rendered inside the parent's space, indented */}
        {!isReply && (
          <div className={`${isRTL ? "pr-10 border-r-2" : "pl-10 border-l-2"} border-[#C86240]/20 space-y-2`}>
            {replies.map((r) => renderComment(r, true))}
            {replyTo === c.id && (
              <div className="bg-white rounded-xl p-2.5 border border-[#C86240]/30">
                <Textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder={isRTL ? `الرد على ${c.user_name}...` : `Reply to ${c.user_name}...`}
                  rows={2}
                  maxLength={2000}
                  dir={isRTL ? "rtl" : "ltr"}
                  className="bg-[#F9F6F0] border-[#2A2421]/15 text-sm"
                  data-testid={`recipe-reply-input-${c.id}`}
                />
                <div className="flex items-center justify-end gap-2 mt-1.5">
                  <Button
                    onClick={() => { setReplyTo(null); setReplyText(""); }}
                    size="sm"
                    variant="ghost"
                    className="rounded-full text-xs"
                    data-testid={`recipe-reply-cancel-${c.id}`}
                  >
                    {isRTL ? "إلغاء" : "Cancel"}
                  </Button>
                  <Button
                    onClick={() => submitReply(c.id)}
                    disabled={replySubmitting || replyText.trim().length < 3}
                    size="sm"
                    className="bg-[#C86240] hover:bg-[#b15637] text-white rounded-full text-xs"
                    data-testid={`recipe-reply-submit-${c.id}`}
                  >
                    <Send className="w-3 h-3 me-1" /> {isRTL ? "نشر الرد" : "Post reply"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mt-4 pt-6 border-t border-[#2A2421]/10" data-testid={`recipe-feedback-${recipeId}`}>
      {/* Rating block */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 bg-white rounded-2xl p-4 border border-[#2A2421]/10">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#5C544F] mb-1">
            {isRTL ? "تقييم الوصفة" : "Rate this recipe"}
          </div>
          <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)} data-testid="recipe-rating-stars">
            {[1, 2, 3, 4, 5].map((n) => {
              const active = (hover || myRating) >= n;
              return (
                <button
                  key={n}
                  type="button"
                  onMouseEnter={() => setHover(n)}
                  onClick={() => setRating(n)}
                  className="p-1 transition-transform hover:scale-110"
                  data-testid={`recipe-rating-star-${n}`}
                  title={`${n}/5`}
                >
                  <Star className={`w-6 h-6 ${active ? "fill-[#C86240] text-[#C86240]" : "text-[#5C544F]/40"}`} />
                </button>
              );
            })}
          </div>
        </div>
        <div className="text-end">
          <div className="text-2xl font-semibold text-[#2A2421]" data-testid="recipe-rating-avg">
            {avg.toFixed(1)} <span className="text-sm text-[#5C544F]">/ 5</span>
          </div>
          <div className="text-[10px] text-[#5C544F] uppercase tracking-widest">
            {count} {isRTL ? "تقييم" : count === 1 ? "rating" : "ratings"}
          </div>
          {raters.length > 0 && (
            <button
              type="button"
              onClick={() => setShowRaters((v) => !v)}
              className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#C86240] hover:text-[#9d4d31] transition-colors"
              data-testid="recipe-raters-toggle"
            >
              <Users className="w-3 h-3" />
              {isRTL ? (showRaters ? "إخفاء قائمة المُقيِّمين" : "عرض من قيّم") : (showRaters ? "Hide raters" : "Who rated?")}
              {showRaters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          )}
        </div>
      </div>

      {showRaters && raters.length > 0 && (
        <div className="-mt-3 mb-6 bg-[#F9F6F0] rounded-2xl p-4 border border-[#2A2421]/10" data-testid="recipe-raters-list">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C544F] mb-3">
            {isRTL ? `قائمة المُقيِّمين (${raters.length})` : `Raters (${raters.length})`}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {raters.map((r) => (
              <div
                key={r.user_id}
                className="flex items-center gap-3 bg-white rounded-xl p-2 border border-[#2A2421]/10"
                data-testid={`recipe-rater-${r.user_id}`}
              >
                {r.user_picture ? (
                  <img src={r.user_picture} alt="" className="w-8 h-8 rounded-full border border-[#2A2421]/10" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#C86240]/15 flex items-center justify-center text-[#C86240] text-xs font-semibold">
                    {(r.user_name || "?").charAt(0)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#2A2421] truncate">{r.user_name}</div>
                  <div className="text-[10px] text-[#5C544F]">
                    {r.at ? new Date(r.at).toLocaleDateString() : ""}
                  </div>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={n}
                      className={`w-3 h-3 ${n <= r.rating ? "fill-[#C86240] text-[#C86240]" : "text-[#5C544F]/30"}`}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Comments header */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-[#C86240]" />
          <h4 className="text-sm uppercase tracking-[0.2em] text-[#2A2421]">
            {isRTL ? `التعليقات (${comments.length})` : `Comments (${comments.length})`}
          </h4>
        </div>
        <div className="inline-flex rounded-full border border-[#2A2421]/15 p-0.5 bg-white text-xs">
          <button
            onClick={() => setSort("top")}
            className={`px-3 py-1 rounded-full flex items-center gap-1 ${sort === "top" ? "bg-[#C86240] text-white" : "text-[#5C544F]"}`}
            data-testid="recipe-comments-sort-top"
          >
            <Flame className="w-3 h-3" /> {isRTL ? "الأكثر تفاعلاً" : "Top"}
          </button>
          <button
            onClick={() => setSort("new")}
            className={`px-3 py-1 rounded-full flex items-center gap-1 ${sort === "new" ? "bg-[#C86240] text-white" : "text-[#5C544F]"}`}
            data-testid="recipe-comments-sort-new"
          >
            <Clock className="w-3 h-3" /> {isRTL ? "الأحدث" : "Latest"}
          </button>
        </div>
      </div>

      {user ? (
        <div className="flex flex-col gap-2 mb-6 bg-white rounded-xl p-3 border border-[#2A2421]/10">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={isRTL ? "شارك تجربتك مع هذه الوصفة..." : "Share your experience with this recipe..."}
            rows={3}
            maxLength={2000}
            dir={isRTL ? "rtl" : "ltr"}
            className="bg-[#F9F6F0] border-[#2A2421]/15"
            data-testid="recipe-comment-input"
          />
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-[#5C544F]">{text.length}/2000</span>
            <Button
              onClick={submit}
              disabled={submitting || text.trim().length < 3}
              size="sm"
              className="bg-[#C86240] hover:bg-[#b15637] text-white rounded-full"
              data-testid="recipe-comment-submit"
            >
              <Send className="w-3.5 h-3.5 me-1" /> {isRTL ? "نشر" : "Post"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="text-sm text-[#5C544F] bg-white rounded-xl p-3 mb-6 flex items-center gap-2 border border-[#2A2421]/10">
          <Sparkles className="w-4 h-4 text-[#C86240]" />
          {isRTL ? "سجّل دخولك لتقيّم الوصفة وتعلق عليها." : "Sign in to rate and comment."}
        </div>
      )}

      <div className="space-y-3">
        {isLoading && <div className="text-sm text-[#5C544F]">…</div>}
        {!isLoading && topLevel.length === 0 && (
          <div className="text-sm text-[#5C544F] italic">
            {isRTL ? "كن أول من يشارك تجربته." : "Be the first to share your experience."}
          </div>
        )}
        {topLevel.map((c) => renderComment(c, false))}
      </div>
    </div>
  );
}
